import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import './TargetedLanding.css';

const TargetedLanding = ({ segment }) => {
  // Define custom content for each high-intent B2B SEO segment
  const segments = {
    'nursing-home': {
      title: 'Nursing Home Software India — Premium HMS Suite',
      metaDesc: 'Digitize your nursing home with India\'s best HMS software. Manage OPD, IPD, bed sheets, diagnostics, CGHS/TPA billing, and ABHA IDs dynamically.',
      canonical: 'https://mediwaynextgen.wayug.com/nursing-home-software-india',
      h1: 'Enterprise Hospital & Nursing Home Software in India',
      subtitle: 'Engineered specifically for Indian private nursing homes and 15-100 bed hospitals. Digitise your OPD, IPD ward boards, pharmacy, diagnostics, and cash collections on a secure cloud suite.',
      icon: '🏥',
      badge: 'NABH Audit & Accreditation Ready',
      schema: {
        '@context': 'https://schema.org',
        '@type': 'Product',
        'name': 'Mediway Nextgen Nursing Home Software',
        'description': 'End-to-end HMS for Indian private nursing homes and clinics featuring EMR, IPD bed sheets, CGHS rate lists, and ABDM health locker sync.',
        'brand': 'Mediway Nextgen',
        'url': 'https://mediwaynextgen.wayug.com/nursing-home-software-india'
      },
      features: [
        { title: 'Interactive Bed Layout Board', desc: 'Real-time floor board mapping for ICU, General Ward, and AC Deluxe rooms to coordinate admissions, discharges, and bed transfers.' },
        { title: 'Corporate TPA & CGHS Billing', desc: 'CGHS/ECHS rate list integration, auto-calculated packages, pre-authorization trackers, and Railway billing compliance.' },
        { title: 'Continuity-of-Care MAR Sheets', desc: 'Hourly vitals monitoring, nurse-assigned schedules, and Medicine Administration Records (MAR) to guarantee patient safety.' },
        { title: 'ABHA/ABDM Health Lockers', desc: 'Instantly issue ABHA IDs, link clinical lab reports, and sync diagnostics to the government network compliant with Ayushman Bharat.' }
      ],
      benefits: [
        { icon: '🛏️', label: '99% Bed Roster Accuracy' },
        { icon: '💸', label: 'Zero Unbilled IPD Leaks' },
        { icon: '🔒', label: 'NABH Audit Compliance' }
      ],
      trustTitle: 'NABH Audit & Compliance Ready',
      trustDesc: 'Our system automatically tracks role-based staff edits, digital patient consent forms, drug allergy warnings, and quality registers. We help you clear NABH and NABL certification audits smoothly with built-in compliance logs.',
      faq: [
        { q: 'Is this system suitable for small 20-bed nursing homes?', a: 'Yes! Mediway Nextgen scales from specialized 15-bed nursing homes up to 150+ bed multi-campus corporate hospitals. You only pay for active beds and modules.' },
        { q: 'How does it help with NABH accreditation?', a: 'We track incident registers, quality indicators, staff logs, and electronic medical consents natively, providing ready-to-export logs for NABH assessors.' }
      ]
    },
    'diagnostic-centre': {
      title: 'Diagnostic Centre Software India — Pathology & Radiology LIS',
      metaDesc: 'Streamline diagnostics, pathology and radiology workflows with India\'s best diagnostic software. Smart barcode phlebotomy and direct machine integrations.',
      canonical: 'https://mediwaynextgen.wayug.com/diagnostic-centre-software-india',
      h1: 'Diagnostic Centre & Laboratory Information System (LIS)',
      subtitle: 'Accelerate patient turnaround times, interface biochem/hematology machines, automate barcode phlebotomy, and securely deliver PDF reports to WhatsApp & SMS.',
      icon: '🔬',
      badge: 'NABL & ISO 15189 Compliant',
      schema: {
        '@context': 'https://schema.org',
        '@type': 'Product',
        'name': 'Mediway Nextgen LIS & Diagnostic Software',
        'description': 'Advanced lab management suite with barcode sample tracking, analyzer interfacing, NABL audit logs, and instant WhatsApp PDF reports.',
        'brand': 'Mediway Nextgen',
        'url': 'https://mediwaynextgen.wayug.com/diagnostic-centre-software-india'
      },
      features: [
        { title: 'Direct Machine Interfacing', desc: 'Connect biochemistry, hematology, immunology, and urine analyzers (via serial/TCP) to dump test results directly without manual entry errors.' },
        { title: 'Barcode Sample Tracker', desc: 'Autogenerates phlebotomy stickers at collection counters to track samples through processing, validation, and electronic signing.' },
        { title: 'Smart PDF WhatsApp delivery', desc: 'Securely publish validated clinical reports straight to patients\' WhatsApp numbers and SMS with high-fidelity encryption.' },
        { title: 'Levey-Jennings Quality Charts', desc: 'Built-in L-J control charts and validation templates designed for strict NABL and ISO 15189 lab quality compliance.' }
      ],
      benefits: [
        { icon: '⚡', label: '60% Faster Report Delivery' },
        { icon: '🚫', label: 'Zero Transcription Errors' },
        { icon: '📊', label: 'NABL-Compliant QC Logs' }
      ],
      trustTitle: 'NABL & ISO 15189 Standard Support',
      trustDesc: 'Engineered in strict alignment with diagnostic laboratory certification rules in India. Mediway Nextgen keeps meticulous control run records, digital doctor sign-offs, sample rejection logs, and Turnaround Time (TAT) analytics.',
      faq: [
        { q: 'Can we connect multiple diagnostic branches to a central lab?', a: 'Absolutely! Our cloud database synchronizes franchise collection centers, processing labs, and clinical reporting portals on a single central dashboard.' },
        { q: 'Do you support radiology reporting alongside pathology?', a: 'Yes. Our diagnostic suite manages X-Ray, Ultrasound, and CT Scan/MRI rosters, radiology templates, and PACS server viewing integrations.' }
      ]
    },
    'small-hospital-emr': {
      title: 'EMR Software for Small Hospitals — Quick EMR & Prescriptions',
      metaDesc: 'India\'s fastest EMR software for small hospitals and polyclinics. Write smart prescriptions, log vitals, and track history in under 60 seconds.',
      canonical: 'https://mediwaynextgen.wayug.com/emr-software-small-hospital',
      h1: 'Smart EMR & Prescription Software for Small Hospitals',
      subtitle: 'Designed specifically for busy Indian practitioners and 10-50 bed facilities. Replace paperwork with intelligent prescription presets, vital graphs, and instant ABHA-linked health records.',
      icon: '📝',
      badge: 'ABHA & ABDM Integrated EMR',
      schema: {
        '@context': 'https://schema.org',
        '@type': 'Product',
        'name': 'Mediway Nextgen EMR & EHR Software',
        'description': 'Cloud EMR for small hospitals and polyclinics in India. Write speed prescriptions, track history, and sync patient records to ABHA digital locker.',
        'brand': 'Mediway Nextgen',
        'url': 'https://mediwaynextgen.wayug.com/emr-software-small-hospital'
      },
      features: [
        { title: '1-Minute Digital Prescriptions', desc: 'Fast, drop-down drug catalog integrated with standard Indian pharmaceutical brands, dosages, frequencies, and instructions.' },
        { title: 'Smart Pediatric & OBG Vitals', desc: 'Interactive growth charts, vaccination schedules, antenatal calendars, and color-coded vital history graphs.' },
        { title: 'Dynamic Complaint Presets', desc: 'Create custom EMR templates for Cardiology, Ortho, Gastro, and general medicine to log symptoms in just three taps.' },
        { title: 'Ayushman Bharat PHR Link', desc: 'Securely upload EMR summaries, referrals, and lab findings directly to the patient\'s government ABHA app.' }
      ],
      benefits: [
        { icon: '🕒', label: 'Prescribe in <60 Seconds' },
        { icon: '📱', label: 'WhatsApp Rx PDFs' },
        { icon: '🧬', label: 'Drug Interaction Alerts' }
      ],
      trustTitle: 'ABDM Milestones & Privacy Certified',
      trustDesc: 'Rest easy knowing that our Electronic Medical Records system implements HIPAA-safe encryption, granular staff role-permissions, audit trails, and Indian NDHM guidelines to secure clinical patient files.',
      faq: [
        { q: 'Is the drug index updated regularly?', a: 'Yes! We maintain an extensive central drug catalog with leading Indian formulations, updating brands, strengths, and standard contraindications monthly.' },
        { q: 'Can doctors write prescriptions on tablets or iPads?', a: 'Yes! Mediway Nextgen is responsive and fully optimized for tablets, iPads, laptops, and desktop computers.' }
      ]
    },
    'abha-integration': {
      title: 'ABHA & ABDM Integrated HMS Software India — Compliant EMR',
      metaDesc: 'Implement ABDM & ABHA integration in your hospital. Sync clinical data, generate ABHA IDs, and settle PM-JAY claims with our certified software.',
      canonical: 'https://mediwaynextgen.wayug.com/abha-integration-hospital',
      h1: 'ABHA & ABDM Compliant Hospital Management Software',
      subtitle: 'Accelerate digital transformation in compliance with the Ayushman Bharat Digital Mission (ABDM). Natively support ABHA ID creation, health lockers (PHR app), and seamless PM-JAY claim processes.',
      icon: '🛡️',
      badge: 'ABDM M1, M2, M3 Certified',
      schema: {
        '@context': 'https://schema.org',
        '@type': 'Product',
        'name': 'Mediway Nextgen ABHA ABDM System',
        'description': 'Certified ABDM M1, M2, M3 compliance software for hospital management systems, supporting health ID registration and encrypted records transmission.',
        'brand': 'Mediway Nextgen',
        'url': 'https://mediwaynextgen.wayug.com/abha-integration-hospital'
      },
      features: [
        { title: 'Instant ABHA Card Registration', desc: 'Generate 14-digit ABHA Health IDs at reception counters using secure Aadhaar OTP or Mobile verification, instantly printing ABHA cards.' },
        { title: 'ABDM M1, M2, and M3 Certification', desc: 'Natively configured to support Health ID generation, Health Information Provider (HIP), and Health Information User (HIU) nodes.' },
        { title: 'Ayushman Bharat PM-JAY Billing', desc: 'Check package eligibility, manage pre-authorization documents, log cashless inpatient claims, and handle national scheme settlements.' },
        { title: 'Encrypted FHIR Health Lockers', desc: 'Transmit and retrieve digital summaries using global HL7 FHIR standards to secure patient transfer records.' }
      ],
      benefits: [
        { icon: '🇮🇳', label: '100% ABDM Compliant' },
        { icon: '🎟️', label: 'Instant ABHA Card PDF' },
        { icon: '💳', label: 'Smooth PM-JAY Settlement' }
      ],
      trustTitle: 'Ayushman Bharat Digital Mission Readiness',
      trustDesc: 'We strictly align with the National Health Authority (NHA) framework. Patient health data is exchanged using secure end-to-end encryption, utilizing ABDM-certified consent managers so clinical data is only shared with active patient consent.',
      faq: [
        { q: 'What is the benefit of generating an ABHA ID?', a: 'ABHA allows patients to save their digital health history (EMR, diagnostic tests, prescriptions) securely on the government cloud, enabling paperless access across any ABDM-integrated facility in India.' },
        { q: 'Does your system connect with the government PM-JAY billing portal?', a: 'Yes! We support transaction mappings, pre-authorization uploads, and claims logs designed to synchronize billing items with PM-JAY criteria.' }
      ]
    }
  };

  const data = segments[segment] || segments['nursing-home'];

  return (
    <section className="targeted-landing">
      <Helmet>
        <title>{data.title}</title>
        <meta name="description" content={data.metaDesc} />
        <link rel="canonical" href={data.canonical} />
        <script type="application/ld+json">
          {JSON.stringify(data.schema)}
        </script>
      </Helmet>

      <div className="landing-hero">
        <div className="container">
          <div className="hero-badge">
            <span className="badge-icon">⚡</span>
            <span>{data.badge || 'Official Enterprise Edition'}</span>
          </div>
          <h1 className="landing-h1">{data.h1}</h1>
          <p className="landing-subtitle">{data.subtitle}</p>
          <div className="hero-actions">
            <Link to="/contact" className="btn btn-primary">Book Free Demo</Link>
            <a href="#features-section" className="btn btn-secondary">Explore Features</a>
          </div>
        </div>
        <div className="hero-background-gradient"></div>
      </div>

      <div id="features-section" className="landing-features">
        <div className="container">
          <h2 className="landing-section-title">Natively Built Features</h2>
          <p className="landing-section-subtitle">Tailored technology to optimize your clinical and financial operations.</p>
          
          <div className="landing-features-grid">
            {data.features.map((feat, index) => (
              <div className="landing-feature-card" key={index}>
                <div className="landing-feature-num">0{index + 1}</div>
                <h3 className="landing-feature-title">{feat.title}</h3>
                <p className="landing-feature-desc">{feat.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="landing-benefits">
        <div className="container">
          <div className="benefits-container">
            {data.benefits.map((benefit, index) => (
              <div className="benefit-item" key={index}>
                <div className="benefit-icon-wrapper">{benefit.icon}</div>
                <span className="benefit-label">{benefit.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="landing-trust">
        <div className="container">
          <div className="trust-card">
            <div className="trust-icon">{data.icon}</div>
            <div className="trust-info">
              <h3 className="trust-title">{data.trustTitle}</h3>
              <p className="trust-desc">{data.trustDesc}</p>
              <Link to="/contact" className="trust-link">Request Compliance Audit →</Link>
            </div>
          </div>
        </div>
      </div>

      <div className="landing-faq">
        <div className="container">
          <h2 className="landing-section-title">Frequently Asked Questions</h2>
          <p className="landing-section-subtitle">Answers to common operational and regulatory queries.</p>
          
          <div className="faq-grid">
            {data.faq.map((item, index) => (
              <div className="faq-card" key={index}>
                <h4 className="faq-q">🤔 {item.q}</h4>
                <p className="faq-a">{item.a}</p>
              </div>
            ))}
          </div>

          <div className="landing-cta">
            <h3>Digitize Your B2B Healthcare Operations Today</h3>
            <p>Join over 5+ hospitals in India leveraging Mediway Nextgen to optimize revenue, NABH compliance, and patient care.</p>
            <div className="cta-buttons">
              <Link to="/contact" className="btn btn-primary">Schedule a Live Demo</Link>
              <Link to="/pricing" className="btn btn-secondary">Check Pricing Plans</Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TargetedLanding;
